/**
 * Settings Page
 * 
 * Shop preferences, limits, and app configuration
 */

import { Page, Card, BlockStack, Text, Banner, InlineStack, Button } from "@shopify/polaris";
import { useState } from "react";

function SettingsPage() {
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    // TODO: Implement save logic in Phase 9
    setTimeout(() => setIsSaving(false), 1000);
  };

  return (
    <Page
      title="Settings"
      subtitle="Configure app preferences and limits"
      primaryAction={{
        content: "Save Settings",
        onAction: handleSave,
        loading: isSaving,
        disabled: true, // Will enable when settings are editable
      }}
    >
      <BlockStack gap="400">
        <Banner tone="success">
          <BlockStack gap="200">
            <Text as="p" fontWeight="semibold">
              ✅ Phase 9.0: Fraud Detection is now available!
            </Text>
            <Text as="p">
              Monitor suspicious activity, view fraud flags, and manage player access.
            </Text>
          </BlockStack>
        </Banner>

        {/* Fraud Detection Quick Access */}
        <Card>
          <BlockStack gap="400">
            <InlineStack align="space-between" blockAlign="center">
              <div>
                <Text as="h2" variant="headingMd">
                  🛡️ Fraud Detection
                </Text>
                <Text as="p" tone="subdued">
                  Monitor and manage fraud flags and blocked players
                </Text>
              </div>
              <Button url="/settings/fraud" variant="primary">
                Open Dashboard
              </Button>
            </InlineStack>
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">
              Rate Limiting (Preview)
            </Text>
            <Text as="p" tone="subdued">
              Current defaults (hardcoded in backend):
            </Text>
            <BlockStack gap="300">
              <InlineStack align="space-between">
                <Text as="p">Max plays per player per campaign</Text>
                <Text as="p" fontWeight="semibold">
                  Unlimited (configured per campaign)
                </Text>
              </InlineStack>
              <InlineStack align="space-between">
                <Text as="p">IP-based rate limit</Text>
                <Text as="p" fontWeight="semibold">
                  100 plays / hour
                </Text>
              </InlineStack>
              <InlineStack align="space-between">
                <Text as="p">Fingerprint-based rate limit</Text>
                <Text as="p" fontWeight="semibold">
                  50 plays / hour
                </Text>
              </InlineStack>
            </BlockStack>
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">
              Fraud Protection (Preview)
            </Text>
            <Text as="p" tone="subdued">
              Current protections (active):
            </Text>
            <BlockStack gap="300">
              <InlineStack gap="200">
                <Text as="p">✅</Text>
                <Text as="p">Device fingerprinting</Text>
              </InlineStack>
              <InlineStack gap="200">
                <Text as="p">✅</Text>
                <Text as="p">IP tracking</Text>
              </InlineStack>
              <InlineStack gap="200">
                <Text as="p">✅</Text>
                <Text as="p">HMAC webhook verification</Text>
              </InlineStack>
              <InlineStack gap="200">
                <Text as="p">✅</Text>
                <Text as="p">Play session idempotency</Text>
              </InlineStack>
              <InlineStack gap="200">
                <Text as="p">✅</Text>
                <Text as="p">Reward grant deduplication</Text>
              </InlineStack>
            </BlockStack>
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">
              Email Notifications (Preview)
            </Text>
            <Text as="p" tone="subdued">
              Future notification preferences:
            </Text>
            <BlockStack gap="300">
              <InlineStack gap="200">
                <Text as="p">📧</Text>
                <Text as="p">Campaign budget alerts</Text>
              </InlineStack>
              <InlineStack gap="200">
                <Text as="p">📧</Text>
                <Text as="p">Fraud detection alerts</Text>
              </InlineStack>
              <InlineStack gap="200">
                <Text as="p">📧</Text>
                <Text as="p">Weekly performance summary</Text>
              </InlineStack>
              <InlineStack gap="200">
                <Text as="p">📧</Text>
                <Text as="p">Low budget warnings</Text>
              </InlineStack>
            </BlockStack>
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">
              App Information
            </Text>
            <BlockStack gap="300">
              <InlineStack align="space-between">
                <Text as="p">App Version</Text>
                <Text as="p" fontWeight="semibold">
                  1.0.0 (Beta)
                </Text>
              </InlineStack>
              <InlineStack align="space-between">
                <Text as="p">Backend API</Text>
                <Text as="p" fontWeight="semibold">
                  beyondgames.shop
                </Text>
              </InlineStack>
              <InlineStack align="space-between">
                <Text as="p">Database</Text>
                <Text as="p" fontWeight="semibold">
                  PostgreSQL 16 (Prisma ORM)
                </Text>
              </InlineStack>
              <InlineStack align="space-between">
                <Text as="p">Hosting</Text>
                <Text as="p" fontWeight="semibold">
                  Caddy + Docker
                </Text>
              </InlineStack>
            </BlockStack>
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">
              Need Help?
            </Text>
            <BlockStack gap="200">
              <InlineStack gap="300">
                <Button url="https://beyondgames.shop/docs" external>
                  Documentation
                </Button>
                <Button url="https://beyondgames.shop/support" external>
                  Contact Support
                </Button>
              </InlineStack>
            </BlockStack>
          </BlockStack>
        </Card>
      </BlockStack>
    </Page>
  );
}

export default SettingsPage;
